import type { FetchLocationResponse } from '@rover/types';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

export const DEVICE_GEOLOCATION_ERROR = {
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  POSITION_UNAVAILABLE: 'POSITION_UNAVAILABLE',
  TIMEOUT: 'TIMEOUT',
  GEOLOCATION_UNSUPPORTED_ERROR: 'GEOLOCATION_UNSUPPORTED_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};
const SUCCESS = 'SUCCESS';
const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 5000,
  maximumAge: Infinity,
};
const LOCATION_TYPE = {
  CURRENT_LOCATION: 'current-location',
};

const parseGeoLocationErrors = (error: any): string => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return DEVICE_GEOLOCATION_ERROR.PERMISSION_DENIED;

    case error.POSITION_UNAVAILABLE:
      return DEVICE_GEOLOCATION_ERROR.POSITION_UNAVAILABLE;

    case error.TIMEOUT:
      return DEVICE_GEOLOCATION_ERROR.TIMEOUT;

    default:
      return DEVICE_GEOLOCATION_ERROR.UNKNOWN_ERROR;
  }
};

const fireResponseReceived = (startTime: number | null | undefined, status: string) => {
  const timeEnd = performance?.now();
  const timeDelta = startTime && timeEnd ? timeEnd - startTime : 0;
  fireDataLayerEvent({
    event: 'search-current-location',
    status,
    // eg: denied, timeout, success
    responseTime: timeDelta,
  });
};

const handleGeolocationSuccess = (
  startTime: number | null | undefined,
  position: any,
  resolve: (arg0: any) => void
) => {
  fireResponseReceived(startTime, SUCCESS);
  return resolve({
    lat: position.coords.latitude.toString(),
    lng: position.coords.longitude.toString(),
    locationAccuracy: position.coords.accuracy.toString(),
    locationType: LOCATION_TYPE.CURRENT_LOCATION,
  });
};

const handleGeolocationError = (
  startTime: number | null | undefined,
  error: any,
  reject: (arg0: any) => void
) => {
  const errorStatus = parseGeoLocationErrors(error);
  fireResponseReceived(startTime, errorStatus);
  return reject(new Error(`Error during location lookup: ${errorStatus}`));
};

export const get = async (): Promise<FetchLocationResponse> => {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      const startTime = performance?.now();
      navigator.geolocation.getCurrentPosition(
        (position) => handleGeolocationSuccess(startTime, position, resolve),
        (error) => handleGeolocationError(startTime, error, reject),
        GEOLOCATION_OPTIONS
      );
    } else {
      reject(
        new Error(
          `Error during location lookup: ${DEVICE_GEOLOCATION_ERROR.GEOLOCATION_UNSUPPORTED_ERROR}`
        )
      );
    }
  });
};
export default {
  get,
};
